import { useState, type ReactNode } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Play, RefreshCw, Users } from "lucide-react";
import toast from "react-hot-toast";
import PageHeader from "@/components/common/PageHeader";
import { automationService } from "@/services/automation.service";

const tabs = ["Segments", "Playbooks", "Runs", "Tasks"] as const;

export default function AutomationCenterPage() {
  const [tab, setTab] = useState<(typeof tabs)[number]>("Segments");
  const client = useQueryClient();
  const [segments, playbooks, runs, tasks] = useQueries({
    queries: [
      {
        queryKey: ["automation-segments"],
        queryFn: automationService.segments,
      },
      {
        queryKey: ["automation-playbooks"],
        queryFn: automationService.playbooks,
      },
      {
        queryKey: ["automation-runs"],
        queryFn: () => automationService.runs(),
      },
      {
        queryKey: ["automation-tasks"],
        queryFn: () => automationService.tasks(),
      },
    ],
  });
  const refresh = () =>
    client.invalidateQueries({
      predicate: (query) => String(query.queryKey[0]).startsWith("automation-"),
    });
  const runRows = runs.data ?? [];
  const taskRows = tasks.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customer success automation"
        subtitle="Reusable cohorts, governed playbooks, and an accountable task queue driven by tenant health."
      />
      <div className="grid gap-4 md:grid-cols-4">
        <Metric
          label="Dynamic segments"
          value={(segments.data ?? []).length}
          icon={Users}
        />
        <Metric
          label="Active playbooks"
          value={
            (playbooks.data ?? []).filter(
              (item: any) => item.status === "ACTIVE",
            ).length
          }
          icon={Play}
        />
        <Metric
          label="Active runs"
          value={runRows.filter((item: any) => item.status === "ACTIVE").length}
          icon={RefreshCw}
        />
        <Metric
          label="Overdue tasks"
          value={
            taskRows.filter(
              (item: any) =>
                item.status === "OPEN" &&
                item.dueAt &&
                new Date(item.dueAt) < new Date(),
            ).length
          }
          icon={CheckCircle2}
        />
      </div>
      <TabBar values={tabs} value={tab} onChange={setTab} />
      {tab === "Segments" && (
        <SegmentsPanel items={segments.data ?? []} onChanged={refresh} />
      )}
      {tab === "Playbooks" && (
        <PlaybooksPanel
          items={playbooks.data ?? []}
          segments={segments.data ?? []}
          onChanged={refresh}
        />
      )}
      {tab === "Runs" && <RunsPanel items={runRows} onChanged={refresh} />}
      {tab === "Tasks" && <TasksPanel items={taskRows} onChanged={refresh} />}
    </div>
  );
}

function SegmentsPanel({
  items,
  onChanged,
}: {
  items: any[];
  onChanged: () => void;
}) {
  const [form, setForm] = useState({
    name: "",
    description: "",
    maxHealthScore: "40",
    inactiveForDays: "",
    riskLevels: "at_risk,critical",
    statuses: "active",
    cadence: "DAILY",
  });
  const [preview, setPreview] = useState<any>(null);
  const rules = () =>
    compact({
      maxHealthScore: form.maxHealthScore
        ? Number(form.maxHealthScore)
        : undefined,
      inactiveForDays: form.inactiveForDays
        ? Number(form.inactiveForDays)
        : undefined,
      riskLevels: split(form.riskLevels),
      statuses: split(form.statuses),
    });
  const previewMutation = useMutation({
    mutationFn: () => automationService.previewSegment(rules()),
    onSuccess: setPreview,
    onError: () => toast.error("Segment preview failed"),
  });
  const create = useMutation({
    mutationFn: () =>
      automationService.createSegment({
        name: form.name,
        description: form.description || undefined,
        rules: rules(),
        evaluationCadence: form.cadence,
      }),
    onSuccess: () => {
      onChanged();
      toast.success("Dynamic segment created");
      setPreview(null);
    },
    onError: () => toast.error("Segment creation failed"),
  });
  const refresh = useMutation({
    mutationFn: automationService.refreshSegment,
    onSuccess: (result: any) => {
      onChanged();
      toast.success(`${result.matchedCount} tenants matched`);
    },
  });

  return (
    <div className="space-y-4">
      <Panel>
        <h3 className="font-bold text-slate-900">Build a governed cohort</h3>
        <p className="mt-1 text-xs text-slate-500">
          All populated conditions must match. Preview before saving.
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Input
            label="Segment name"
            value={form.name}
            onChange={(value) => setForm({ ...form, name: value })}
          />
          <Input
            label="Max health score"
            value={form.maxHealthScore}
            onChange={(value) => setForm({ ...form, maxHealthScore: value })}
          />
          <Input
            label="Inactive at least days"
            value={form.inactiveForDays}
            onChange={(value) => setForm({ ...form, inactiveForDays: value })}
          />
          <Input
            label="Risk levels"
            value={form.riskLevels}
            onChange={(value) => setForm({ ...form, riskLevels: value })}
          />
          <Input
            label="Tenant statuses"
            value={form.statuses}
            onChange={(value) => setForm({ ...form, statuses: value })}
          />
          <Select
            label="Refresh cadence"
            value={form.cadence}
            values={["HOURLY", "DAILY", "WEEKLY"]}
            onChange={(value) => setForm({ ...form, cadence: value })}
          />
          <div className="md:col-span-2">
            <Input
              label="Description"
              value={form.description}
              onChange={(value) => setForm({ ...form, description: value })}
            />
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <Button
            secondary
            disabled={previewMutation.isPending}
            onClick={() => previewMutation.mutate()}
          >
            Preview
          </Button>
          <Button
            disabled={!form.name || !preview || create.isPending}
            onClick={() => create.mutate()}
          >
            Create segment
          </Button>
        </div>
        {preview && (
          <div className="mt-4 rounded-xl bg-slate-50 p-4">
            <p className="text-sm font-bold">
              {preview.matchedCount} tenants match
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {preview.sample?.slice(0, 12).map((tenant: any) => (
                <span
                  key={tenant.tenantId}
                  className="rounded-full bg-white px-3 py-1 text-xs text-slate-600 shadow-sm"
                >
                  {tenant.tenantName} · {tenant.healthScore ?? "—"}
                </span>
              ))}
            </div>
          </div>
        )}
      </Panel>
      <div className="space-y-3">
        {items.length ? (
          items.map((item) => (
            <Panel key={item.id}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold">{item.name}</h3>
                    <Badge value={item.isActive ? "ACTIVE" : "PAUSED"} />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {item.description || "No description"}
                  </p>
                  <p className="mt-2 text-xs text-slate-400">
                    {item._count?.memberships ?? 0} members ·{" "}
                    {item.evaluationCadence} · last refreshed{" "}
                    {item.lastEvaluatedAt
                      ? new Date(item.lastEvaluatedAt).toLocaleString()
                      : "never"}
                  </p>
                  <code className="mt-2 block max-w-3xl overflow-x-auto rounded-lg bg-slate-50 p-2 text-[10px] text-slate-500">
                    {JSON.stringify(item.rules)}
                  </code>
                </div>
                <Button
                  secondary
                  disabled={refresh.isPending}
                  onClick={() => refresh.mutate(item.id)}
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Refresh now
                </Button>
              </div>
            </Panel>
          ))
        ) : (
          <Empty text="No custom tenant segments yet." />
        )}
      </div>
    </div>
  );
}

function PlaybooksPanel({
  items,
  segments,
  onChanged,
}: {
  items: any[];
  segments: any[];
  onChanged: () => void;
}) {
  const [form, setForm] = useState({
    name: "",
    description: "",
    triggerType: "MANUAL",
    segmentId: "",
    steps:
      '[{"key":"review","title":"Review tenant 360","taskType":"MANUAL","delayHours":0},{"key":"contact","title":"Contact primary owner","taskType":"OUTREACH","delayHours":24}]',
  });
  const create = useMutation({
    mutationFn: () =>
      automationService.createPlaybook({
        name: form.name,
        description: form.description || undefined,
        triggerType: form.triggerType,
        triggerConfig:
          form.triggerType === "SEGMENT_ENTRY"
            ? { segmentId: form.segmentId }
            : undefined,
        steps: JSON.parse(form.steps),
      }),
    onSuccess: () => {
      onChanged();
      toast.success("Playbook draft created");
    },
    onError: () => toast.error("Check the playbook steps JSON"),
  });
  const status = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) =>
      automationService.setPlaybookStatus(
        id,
        value,
        "Administrator approved this playbook status change",
      ),
    onSuccess: () => {
      onChanged();
      toast.success("Playbook status updated");
    },
  });
  const launch = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Record<string, unknown>;
    }) => automationService.launchPlaybook(id, payload),
    onSuccess: (result: any) => {
      onChanged();
      toast.success(`${result.created?.length ?? 0} runs launched`);
    },
  });
  const launchOne = (item: any) => {
    const scope = window
      .prompt("Enter tenant UUID, or type segment:<segment UUID>")
      ?.trim();
    if (!scope) return;
    const payload = scope.startsWith("segment:")
      ? {
          segmentId: scope.slice(8),
          reason: "Administrator launched playbook for a governed segment",
        }
      : {
          tenantId: scope,
          reason: "Administrator launched playbook for an individual tenant",
        };
    launch.mutate({ id: item.id, payload });
  };

  return (
    <div className="space-y-4">
      <Panel>
        <h3 className="font-bold">Create playbook draft</h3>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Input
            label="Name"
            value={form.name}
            onChange={(value) => setForm({ ...form, name: value })}
          />
          <Select
            label="Trigger"
            value={form.triggerType}
            values={[
              "MANUAL",
              "SEGMENT_ENTRY",
              "HEALTH_RISK",
              "LIFECYCLE_STAGE",
            ]}
            onChange={(value) => setForm({ ...form, triggerType: value })}
          />
          {form.triggerType === "SEGMENT_ENTRY" && (
            <Select
              label="Segment"
              value={form.segmentId}
              values={["", ...segments.map((item) => item.id)]}
              labels={["Select segment", ...segments.map((item) => item.name)]}
              onChange={(value) => setForm({ ...form, segmentId: value })}
            />
          )}
          <Input
            label="Description"
            value={form.description}
            onChange={(value) => setForm({ ...form, description: value })}
          />
        </div>
        <label className="mt-3 block">
          <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
            Steps JSON
          </span>
          <textarea
            rows={5}
            value={form.steps}
            onChange={(event) =>
              setForm({ ...form, steps: event.target.value })
            }
            className="w-full rounded-xl border border-slate-200 p-3 font-mono text-xs"
          />
        </label>
        <Button
          disabled={!form.name || create.isPending}
          onClick={() => create.mutate()}
        >
          Create draft
        </Button>
      </Panel>
      <div className="space-y-3">
        {items.length ? (
          items.map((item) => (
            <Panel key={item.id}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold">{item.name}</h3>
                    <Badge value={item.status} />
                    <Badge value={item.triggerType} />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {item.description || "No description"}
                  </p>
                  <p className="mt-2 text-xs text-slate-400">
                    {Array.isArray(item.steps) ? item.steps.length : 0} steps ·{" "}
                    {item._count?.runs ?? 0} historical runs
                  </p>
                </div>
                <div className="flex gap-2">
                  {item.status === "DRAFT" && (
                    <Button
                      secondary
                      onClick={() =>
                        status.mutate({ id: item.id, value: "ACTIVE" })
                      }
                    >
                      Activate
                    </Button>
                  )}
                  {item.status === "ACTIVE" && (
                    <>
                      <Button
                        secondary
                        onClick={() =>
                          status.mutate({ id: item.id, value: "PAUSED" })
                        }
                      >
                        Pause
                      </Button>
                      <Button onClick={() => launchOne(item)}>
                        <Play className="h-3.5 w-3.5" />
                        Launch
                      </Button>
                    </>
                  )}
                  {item.status === "PAUSED" && (
                    <Button
                      secondary
                      onClick={() =>
                        status.mutate({ id: item.id, value: "ACTIVE" })
                      }
                    >
                      Resume
                    </Button>
                  )}
                </div>
              </div>
            </Panel>
          ))
        ) : (
          <Empty text="No customer-success playbooks yet." />
        )}
      </div>
    </div>
  );
}

function RunsPanel({
  items,
  onChanged,
}: {
  items: any[];
  onChanged: () => void;
}) {
  const cancel = useMutation({
    mutationFn: (id: string) =>
      automationService.cancelRun(
        id,
        "Administrator cancelled this active playbook run",
      ),
    onSuccess: () => {
      onChanged();
      toast.success("Run cancelled");
    },
  });
  return (
    <div className="space-y-3">
      {items.length ? (
        items.map((item) => (
          <Panel key={item.id}>
            <div className="flex flex-wrap justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold">{item.playbook?.name}</h3>
                  <Badge value={item.status} />
                </div>
                <p className="mt-1 text-sm text-slate-600">
                  {item.tenant?.name} · {item.tenant?.email}
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  Step {item.currentStep + 1} · started{" "}
                  {new Date(item.startedAt).toLocaleString()} ·{" "}
                  {item.triggerSource}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {item.tasks?.map((task: any) => (
                    <span
                      key={task.id}
                      className={`rounded-full px-2 py-1 text-[10px] font-bold ${task.status === "COMPLETED" ? "bg-emerald-50 text-emerald-700" : task.status === "OPEN" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-500"}`}
                    >
                      {task.sequence + 1}. {task.title}
                    </span>
                  ))}
                </div>
              </div>
              {item.status === "ACTIVE" && (
                <Button secondary onClick={() => cancel.mutate(item.id)}>
                  Cancel
                </Button>
              )}
            </div>
          </Panel>
        ))
      ) : (
        <Empty text="No playbook runs." />
      )}
    </div>
  );
}

function TasksPanel({
  items,
  onChanged,
}: {
  items: any[];
  onChanged: () => void;
}) {
  const complete = useMutation({
    mutationFn: (id: string) =>
      automationService.completeTask(
        id,
        window.prompt("Completion note") || "Task completed by administrator",
      ),
    onSuccess: () => {
      onChanged();
      toast.success("Task completed and next step released");
    },
  });
  return (
    <div className="space-y-3">
      {items.length ? (
        items.map((item) => (
          <Panel key={item.id}>
            <div className="flex flex-wrap justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold">{item.title}</h3>
                  <Badge value={item.status} />
                </div>
                <p className="mt-1 text-sm text-slate-600">
                  {item.tenant?.name} · {item.run?.playbook?.name}
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  Due{" "}
                  {item.dueAt
                    ? new Date(item.dueAt).toLocaleString()
                    : "not set"}{" "}
                  · {item.taskType} · {item.assignedTo || "unassigned"}
                </p>
              </div>
              {item.status === "OPEN" && (
                <Button onClick={() => complete.mutate(item.id)}>
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Complete
                </Button>
              )}
            </div>
          </Panel>
        ))
      ) : (
        <Empty text="No playbook tasks." />
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number;
  icon: any;
}) {
  return (
    <Panel>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500">{label}</p>
          <p className="mt-1 text-2xl font-black text-slate-900">{value}</p>
        </div>
        <Icon className="h-5 w-5 text-blue-600" />
      </div>
    </Panel>
  );
}
function TabBar<T extends readonly string[]>({
  values,
  value,
  onChange,
}: {
  values: T;
  value: T[number];
  onChange: (value: T[number]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-2">
      {values.map((item) => (
        <button
          key={item}
          onClick={() => onChange(item)}
          className={`rounded-xl px-4 py-2 text-xs font-bold ${value === item ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50"}`}
        >
          {item}
        </button>
      ))}
    </div>
  );
}
function Panel({ children }: { children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      {children}
    </section>
  );
}
function Empty({ text }: { text: string }) {
  return (
    <Panel>
      <p className="text-sm text-slate-500">{text}</p>
    </Panel>
  );
}
function Badge({ value }: { value: string }) {
  return (
    <span className="rounded-full bg-slate-100 px-2 py-1 text-[9px] font-bold uppercase text-slate-600">
      {value}
    </span>
  );
}
function Button({
  children,
  onClick,
  disabled,
  secondary = false,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  secondary?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-bold disabled:opacity-50 ${secondary ? "border border-slate-200 bg-white text-slate-700" : "bg-slate-900 text-white"}`}
    >
      {children}
    </button>
  );
}
function Input({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label>
      <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
        {label}
      </span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
      />
    </label>
  );
}
function Select({
  label,
  value,
  values,
  labels,
  onChange,
}: {
  label: string;
  value: string;
  values: string[];
  labels?: string[];
  onChange: (value: string) => void;
}) {
  return (
    <label>
      <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
      >
        {values.map((item, index) => (
          <option key={item} value={item}>
            {labels?.[index] ?? item}
          </option>
        ))}
      </select>
    </label>
  );
}
function split(value: string) {
  const result = value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  return result.length ? result : undefined;
}
function compact(value: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(value).filter(([, item]) => item !== undefined),
  );
}
